import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  DialogContentText,
} from "@mui/material";
import User from "../../../../types/BackendUser";

export default function CustomUserDialog({
  open,
  onClose,
  user,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  user: any;
  onSave: (updatedUser: User) => void;
}) {
  const [formData, setFormData] = useState<User>({
    givenName: "",
    familyName: "",
    username: "",
    groupName: "",
    employeeType: "",
  });
  const [errors, setErrors] = useState({
    givenName: "",
    familyName: "",
    username: "",
    groupName: "",
    employeeType: "",
  });
  const [showEmailChangeConfirm, setShowEmailChangeConfirm] = useState(false);

  const isEditMode = !!user;
  const emailChanged =
    isEditMode &&
    user?.email &&
    formData.username.trim() !== "" &&
    formData.username.trim().toLowerCase() !== user.email.trim().toLowerCase();

  useEffect(() => {
    if (user) {
      setFormData({
        givenName: user.name.split(" ")[0] || "",
        familyName: user.name.split(" ")[1] || "",
        username: user.email || "",
        groupName: user.role || "",
        employeeType: user.type || "",
      });
    } else {
      setFormData({
        givenName: "",
        familyName: "",
        username: "",
        groupName: "",
        employeeType: "",
      });
    }
    setShowEmailChangeConfirm(false);
  }, [user]);

  const handleTextChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "", // Clear error when user types
      }));
    };

  const handleSelectChange =
    (field: string) => (e: SelectChangeEvent<string>) => {
      setFormData({ ...formData, [field]: e.target.value });

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "", // Clear error when user types
      }));
    };

  const handleSave = () => {
    if (!validate()) return;

    if (emailChanged) {
      setShowEmailChangeConfirm(true);
      return;
    }

    submitSave();
  };

  const submitSave = () => {
    const payload: User = { ...formData };
    if (emailChanged && user?.email) {
      payload.previousUsername = user.email;
    }
    onSave(payload);
    setShowEmailChangeConfirm(false);
    onClose();
  };

  const validate = () => {
    const newErrors: typeof errors = {
      givenName: "",
      familyName: "",
      username: "",
      groupName: "",
      employeeType: "",
    };
    let isValid = true;

    if (!formData.givenName.trim()) {
      newErrors.givenName = "Name is required.";
      isValid = false;
    }

    if (!formData.familyName.trim()) {
      newErrors.familyName = "Surname is required.";
      isValid = false;
    }

    if (!formData.username.trim()) {
      newErrors.username = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.username = "Invalid email format.";
      isValid = false;
    }

    if (!formData.groupName) {
      newErrors.groupName = "Role is required.";
      isValid = false;
    }

    if (!formData.employeeType) {
      newErrors.employeeType = "Employee Type is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onClose();
          },
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            letterSpacing: 0.8,
            fontWeight: 700,
          }}
        >
          {isEditMode ? "Edit User" : "Add User"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth>
          <FormLabel htmlFor="givenName">Name</FormLabel>
          <TextField
            value={formData.givenName || ""}
            error={Boolean(errors.givenName)}
            placeholder="Name"
            onChange={handleTextChange("givenName")}
          />
          {errors.givenName && (
            <p style={{ color: "red", fontSize: "10px" }}>{errors.givenName}</p>
          )}
        </FormControl>
        <FormControl fullWidth>
          <FormLabel htmlFor="familyName">Surname</FormLabel>
          <TextField
            value={formData.familyName || ""}
            error={Boolean(errors.familyName)}
            placeholder="Surname"
            onChange={handleTextChange("familyName")}
          />
          {errors.familyName && (
            <p style={{ color: "red", fontSize: "10px" }}>
              {errors.familyName}
            </p>
          )}
        </FormControl>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <FormLabel htmlFor="username">Email</FormLabel>
          <TextField
            value={formData.username || ""}
            error={Boolean(errors.username)}
            placeholder="Email"
            type="email"
            onChange={handleTextChange("username")}
          />
          {errors.username && (
            <p style={{ color: "red", fontSize: "10px" }}>{errors.username}</p>
          )}
        </FormControl>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <FormLabel htmlFor="groupName">Role</FormLabel>
          <Select
            id="role"
            name="role"
            value={formData.groupName}
            displayEmpty
            renderValue={(selected) =>
              selected ? (
                selected
              ) : (
                <span style={{ opacity: 0.6 }}>Select a role</span>
              )
            }
            error={Boolean(errors.groupName)}
            onChange={handleSelectChange("groupName")}
            sx={{
              background: "#fff",
            }}
          >
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </Select>
          {errors.groupName && (
            <p style={{ color: "red", fontSize: "10px" }}>{errors.groupName}</p>
          )}
        </FormControl>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <FormLabel htmlFor="employeeType">Employee Type</FormLabel>
          <Select
            id="type"
            name="type"
            value={formData.employeeType}
            error={Boolean(errors.employeeType)}
            displayEmpty
            renderValue={(selected) =>
              selected ? (
                selected
              ) : (
                <span style={{ opacity: 0.6 }}>Select a type</span>
              )
            }
            onChange={handleSelectChange("employeeType")}
            sx={{
              background: "#fff",
            }}
          >
            <MenuItem value="Employee">Employee</MenuItem>
            <MenuItem value="Contractor">Contractor</MenuItem>
          </Select>
          {errors.employeeType && (
            <p style={{ color: "red", fontSize: "10px" }}>
              {errors.employeeType}
            </p>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {isEditMode ? "Save Changes" : "Add User"}
        </Button>
      </DialogActions>

      {/* Confirmation when email is changed: user will receive verification email */}
      <Dialog
        open={showEmailChangeConfirm}
        onClose={() => setShowEmailChangeConfirm(false)}
      >
        <DialogTitle>Change email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The user will receive a verification email at the new address. Do you
            want to save?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEmailChangeConfirm(false)}>
            Cancel
          </Button>
          <Button onClick={submitSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
